'use client'

import React, { useEffect, useRef } from 'react'
import { MapPin } from 'lucide-react'

import { CityMapHoverCard } from '@/components/Location/CityMapHoverCard'
import { LocationMapHoverCard } from '@/components/Location/LocationMapHoverCard'
import { MapHoverCardPortal } from '@/components/Location/MapHoverCardPortal'
import type { MapCityCardData } from '@/lib/cities/mapCityData'
import type { MapLocationCardData } from '@/lib/locations/mapLocationData'
import { useMessages } from '@/lib/i18n/useMessages'
import { MapMarker, MarkerContent } from '@/components/ui/map'
import { cn } from '@/utilities/ui'

type MapPinHoverCardProps = {
  cardId: string
  cardType: 'city' | 'location'
  cityCard?: MapCityCardData
  isActive?: boolean
  label: string
  latitude: number
  locationCard?: MapLocationCardData
  longitude: number
  markerClassName?: string
  onActivate: () => void
  onDeactivate: () => void
  viewCityLabel?: string
  visible?: boolean
}

export function MapPinHoverCard({
  cardId,
  cardType,
  cityCard,
  isActive = false,
  label,
  latitude,
  locationCard,
  longitude,
  markerClassName,
  onActivate,
  onDeactivate,
  viewCityLabel = 'View City',
  visible = true,
}: MapPinHoverCardProps) {
  const closeLabel = useMessages().common.close
  const anchorRef = useRef<HTMLDivElement>(null)
  const deactivateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleActivate = () => {
    if (deactivateTimeoutRef.current) {
      clearTimeout(deactivateTimeoutRef.current)
      deactivateTimeoutRef.current = null
    }

    onActivate()
  }

  const handleDeactivate = () => {
    deactivateTimeoutRef.current = setTimeout(() => {
      onDeactivate()
    }, 120)
  }

  const handleClose = () => {
    if (deactivateTimeoutRef.current) {
      clearTimeout(deactivateTimeoutRef.current)
      deactivateTimeoutRef.current = null
    }

    onDeactivate()
  }

  useEffect(() => {
    return () => {
      if (deactivateTimeoutRef.current) {
        clearTimeout(deactivateTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <MapMarker anchor="bottom" latitude={latitude} longitude={longitude}>
        <MarkerContent className={cn('pointer-events-auto', !visible && 'invisible opacity-0')}>
          <div
            ref={anchorRef}
            className="relative flex flex-col items-center"
            data-location-map-pin-active={isActive ? 'true' : undefined}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                handleDeactivate()
              }
            }}
            onMouseEnter={() => handleActivate()}
            onMouseLeave={() => handleDeactivate()}
          >
            <button
              aria-controls={cardId}
              aria-expanded={isActive}
              aria-label={label}
              className={cn(
                markerClassName,
                'cursor-pointer transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
                isActive && 'ring-2 ring-turquoise/40',
              )}
              onClick={() => handleActivate()}
              type="button"
            >
              <MapPin aria-hidden className="size-4 shrink-0 text-[#B4F0DD]" />
              <span className="truncate">{label}</span>
            </button>
          </div>
        </MarkerContent>
      </MapMarker>

      {isActive ? (
        <MapHoverCardPortal
          anchorRef={anchorRef}
          isActive={isActive}
          onClose={handleClose}
          onFocus={() => handleActivate()}
          onMouseEnter={() => handleActivate()}
          onMouseLeave={() => handleDeactivate()}
          positionDeps={[latitude, longitude]}
        >
          {cardType === 'city' && cityCard ? (
            <CityMapHoverCard
              city={cityCard}
              closeLabel={closeLabel}
              id={cardId}
              onClose={handleClose}
              viewCityLabel={viewCityLabel}
            />
          ) : null}
          {cardType === 'location' && locationCard ? (
            <LocationMapHoverCard
              card={locationCard}
              closeLabel={closeLabel}
              id={cardId}
              onClose={handleClose}
            />
          ) : null}
        </MapHoverCardPortal>
      ) : null}
    </>
  )
}
